import { useEffect, useState } from 'react';
import { addOnChargesPersBody } from '../../../common/AdvanceBodies';
import { getPersColor, getPersFont, getPersLocation, getPersNote } from '../../../helpers/product';
import { FullProduct, ProductDropDownValue, ProductSettings } from '../../../models/Product';
import * as advanceAPIUtil from '../../../util/advance/advanceAPIUtil';

type PersonalizationRow = {
  color: string;
  displayText: string;
  font: string;
  itemCode: string;
  itemColor: string;
  itemId: string;
  itemNo: string;
  itemSize: string;
  location: string;
  notes: string;
  orderId: string;
  rowId: number;
  rowQtyId: string;
  rowQtyNo: number;
  sequance: number;
  lineName?: string;
  isDrp?: boolean;
  drpValue?: string[];
  isEnabled?: boolean;
  requiredLine?: string;
  cloneKey?: number;
};

type UseProductPersonalizationStateProps = {
  items: any[];
  product: FullProduct;
  productSettings: ProductSettings;
  setLoading: (loading: boolean) => void;
  getSettingStatus: (key: string) => boolean;
};

function useProductPersonalizationState({
  items,
  product,
  productSettings,
  setLoading,
  getSettingStatus,
}: UseProductPersonalizationStateProps) {
  const isApplyToAllPersonalizationLines =
    product?.applyToAllPersonalizationLines !== null &&
    product?.applyToAllPersonalizationLines !== undefined
      ? product?.applyToAllPersonalizationLines
      : productSettings?.applyToAllPersonalizationLines;

  const [personalizationCost, setPersonalizationCost] = useState<{
    matchOrderQty: number;
    price: number;
    setupPrice: number;
    chargeId: string;
  }>({
    matchOrderQty: 0,
    price: 0,
    setupPrice: 0,
    chargeId: '',
  });

  const [personalizationArray, setPersonalizationArray] = useState<PersonalizationRow[] | null>(
    null,
  );
  const [personalizationPerUnitPrice, setPersonalizationPerUnitPrice] = useState<string | null>(
    null,
  );
  const [personalizationSetupFee, setPersonalizationSetupFee] = useState<string | null>(null);
  const [personalizationDropDown, setPersonalizationDropDown] = useState<boolean | null>(null);
  const [personalizationRequired, setPersonalizationRequired] = useState<boolean | null>(null);
  const [requiredLinesPersonalization, setRequiredLinesPersonalization] = useState<string | null>(
    null,
  );
  const [dropDownValues, setDropDownValues] = useState<ProductDropDownValue[]>([]);
  const [loadPersonalizationCost, setLoadPersonalizationCost] = useState<boolean>(false);
  const [advanceAddonCharge, setAdvanceAddonCharge] = useState<boolean>(false);
  const [showPersonalizationCostInCart, setShowPersonalizationCostInCart] =
    useState<boolean>(false);
  const [personalizationLabel, setPersonalizationLabel] = useState<string | null>(null);
  const [showPersonalization, setShowPersonalization] = useState<boolean>(false);
  const [itemsAdded, setItemsAdded] = useState<number[]>([]);
  const [addAll, setAddAll] = useState<boolean>(false);

  /** ---------- helpers ---------- */
  const getGroupKey = (r: PersonalizationRow) => `${r.rowId}-${r.rowQtyNo}-${r.itemSize}`;

  /** Keep sequence continuous within each (rowId,rowQtyNo,itemSize) group and sort overall */
  function resequenceRows(rows: PersonalizationRow[]): PersonalizationRow[] {
    const groups = rows.reduce((acc, r) => {
      const groupKey = getGroupKey(r);
      (acc[groupKey] ||= []).push(r);
      return acc;
    }, {} as Record<string, PersonalizationRow[]>);

    const result: PersonalizationRow[] = [];
    Object.values(groups).forEach((g) => {
      g.sort((a, b) => a.sequance - b.sequance).forEach((row, i) => {
        result.push({ ...row, sequance: i + 1 });
      });
    });

    return result.sort((a, b) => {
      if (a.rowId !== b.rowId) return Number(a.rowId) - Number(b.rowId);
      if (a.rowQtyNo !== b.rowQtyNo) return a.rowQtyNo - b.rowQtyNo;
      return a.sequance - b.sequance;
    });
  }

  /** ---------- effects ---------- */
  useEffect(() => {
    if (items && (itemsAdded.length > 0 || addAll)) {
      createPersonalizationRows(isApplyToAllPersonalizationLines);
    }
  }, [items, itemsAdded, addAll, isApplyToAllPersonalizationLines]);

  useEffect(() => {
    setPersonalizationPerUnitPrice(product?.personalizationPerUnitPrice);
    setPersonalizationSetupFee(product?.personalizationSetupFee);
  }, [product]);

  useEffect(() => {
    if (product?.personalizationDropDown === true || product?.personalizationDropDown === false) {
      setPersonalizationDropDown(product.personalizationDropDown);
      setRequiredLinesPersonalization(product.requiredLinesPersonalization);
      setDropDownValues(product.dropDownValues);
    } else if (
      productSettings?.personalizationDropDown === true ||
      productSettings?.personalizationDropDown === false
    ) {
      setPersonalizationDropDown(productSettings.personalizationDropDown);
      setRequiredLinesPersonalization(productSettings.requiredLinesPersonalization);
      setDropDownValues(productSettings.dropDownValues);
    }
    if (getSettingStatus) {
      setPersonalizationRequired(getSettingStatus('personalizationRequired'));
      setShowPersonalizationCostInCart(getSettingStatus('showPersonalizationCostInCart'));
    }
    setPersonalizationLabel(
      product?.productPersLabel
        ? product.productPersLabel
        : productSettings?.['showPersonalizationLabel'] || 'Personalization',
    );
    setShowPersonalization(getSettingStatus('showPersonalization'));
  }, [product, productSettings, getSettingStatus]);

  useEffect(() => {
    if (personalizationRequired) {
      addPersonalization(0);
    }
  }, [personalizationRequired]);

  useEffect(() => {
    if (advanceAddonCharge) {
      setPersonalizationCost((prev) => ({
        ...prev,
        matchOrderQty: 1,
        price: personalizationPerUnitPrice ? Number(personalizationPerUnitPrice) : 0,
        setupPrice: personalizationSetupFee ? Number(personalizationSetupFee) : 0,
        chargeId: '',
      }));
    }
  }, [advanceAddonCharge, personalizationPerUnitPrice, personalizationSetupFee]);

  const fetchPersonalizationCost = async () => {
    setLoading(true);
    setLoadPersonalizationCost(true);
    try {
      const result = await advanceAPIUtil.customAdvanceCall(addOnChargesPersBody);
      if (result?.data?.data) {
        const personalizationData = result.data.data.filter((val: any) => val.itemCode === 'PERS');
        if (personalizationData) setAdvanceAddonCharge(true);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const addPersonalization = (key: number) => {
    if (key !== undefined) {
      if (itemsAdded.includes(key)) return;
      setItemsAdded((pv) => [...pv, key]);
    } else {
      setItemsAdded([]);
      setAddAll(true);
    }
  };

  const changePersonalizationArray = (
    value: string,
    type: keyof PersonalizationRow,
    row: PersonalizationRow,
  ) => {
    if (!personalizationArray) return;
    let updated = [...personalizationArray];

    if (isApplyToAllPersonalizationLines) {
      // update all rows of the SAME SIZE at the SAME sequence
      updated = updated.map((r) =>
        r.itemSize === row.itemSize && r.sequance === row.sequance ? { ...r, [type]: value } : r,
      );
    } else {
      // only the selected row
      updated = updated.map((r) =>
        r.rowId === row.rowId &&
        r.rowQtyNo === row.rowQtyNo &&
        r.itemSize === row.itemSize &&
        r.sequance === row.sequance
          ? { ...r, [type]: value }
          : r,
      );
    }

    setPersonalizationArray(updated);
  };

  /** ---------- ADD ---------- */
  const addPersonalizationRow = (row: PersonalizationRow) => {
    if (!personalizationArray) return;

    let updated = [...personalizationArray];

    if (isApplyToAllPersonalizationLines) {
      // One new row per unique (rowId,rowQtyNo,itemSize) group with the SAME SIZE
      const targetsByKey = new Map<string, PersonalizationRow[]>();
      personalizationArray
        .filter((r) => r.itemSize === row.itemSize)
        .forEach((r) => {
          const groupKey = getGroupKey(r);
          const arr = targetsByKey.get(groupKey) || [];
          arr.push(r);
          targetsByKey.set(groupKey, arr);
        });

      targetsByKey.forEach((group) => {
        const maxSeq = Math.max(...group.map((g) => g.sequance), 0);
        const template = group[group.length - 1];
        updated.push({
          ...template,
          displayText: '',
          color: getPersColor(product, productSettings),
          font: getPersFont(product, productSettings),
          location: getPersLocation(product, productSettings),
          notes: getPersNote(product, productSettings),
          sequance: maxSeq + 1,
        });
      });
    } else {
      // Insert AFTER the selected index within the same (rowId,rowQtyNo,itemSize) group
      const groupKey = getGroupKey(row);

      const insertSeq = row.sequance + 1;

      // Shift all rows at or after insertSeq by +1
      updated = updated.map((r) =>
        getGroupKey(r) === groupKey && r.sequance >= insertSeq
          ? { ...r, sequance: r.sequance + 1 }
          : r,
      );

      // Insert the new row at insertSeq
      updated.push({
        ...row,
        displayText: '',
        color: getPersColor(product, productSettings),
        font: getPersFont(product, productSettings),
        location: getPersLocation(product, productSettings),
        notes: getPersNote(product, productSettings),
        sequance: insertSeq,
      });
    }

    setPersonalizationArray(resequenceRows(updated));
  };

  /** ---------- DELETE ---------- */
  // Accepts either an index OR a row object (so existing callers still work)
  const deletePersonalizationRow = (target: number | PersonalizationRow) => {
    if (!personalizationArray || personalizationArray.length === 0) return;

    const targetRow: PersonalizationRow | undefined =
      typeof target === 'number' ? personalizationArray[target] : target;

    if (!targetRow) return;

    let updated = [...personalizationArray];

    if (isApplyToAllPersonalizationLines) {
      // Remove all rows with SAME SIZE & SAME sequence
      updated = updated.filter(
        (r) => !(r.itemSize === targetRow.itemSize && r.sequance === targetRow.sequance),
      );
    } else {
      // Remove only within the same (rowId,rowQtyNo,itemSize) group at that sequence
      const groupKey = getGroupKey(targetRow);
      updated = updated.filter(
        (r) => !(getGroupKey(r) === groupKey && r.sequance === targetRow.sequance),
      );
    }

    setPersonalizationArray(resequenceRows(updated));
  };

  /** ---------- INITIAL CREATE ---------- */

  const createPersonalizationRows = (clear = false) => {
    if (!loadPersonalizationCost) {
      fetchPersonalizationCost();
    }
    try {
      let updatedPersonalizationArray: PersonalizationRow[] = [];
      // Process each item
      for (let key = 0; key < items.length; key++) {
        if (!addAll && !itemsAdded.includes(key)) continue;
        for (const row of items[key]) {
          const persQty = Number(row.quantity);
          if (persQty <= 0) continue;
          // Get existing main rows for this item
          const tempMainRows = personalizationArray?.filter(
            (persRow) => row.id === persRow.rowId && Number(persRow.sequance) === 1,
          );
          // Get first row values for isApplyToAll functionality
          const firstRowValues = tempMainRows?.[0]
            ? personalizationArray?.filter(
                (persRow) => persRow.rowQtyNo === tempMainRows[0].rowQtyNo,
              )
            : null;
          const pushedIds = new Set<number>();
          // Process each quantity
          for (let index = 0; index < persQty; index++) {
            const mainRow = tempMainRows?.[index];
            if (mainRow && !pushedIds.has(mainRow.rowQtyNo)) {
              // Handle existing personalization rows
              pushedIds.add(mainRow.rowQtyNo);
              const personalizationRows = personalizationArray?.filter(
                (persRow) => row.id === persRow.rowId && persRow.rowQtyNo === mainRow.rowQtyNo,
              );
              for (const r of personalizationRows ?? []) {
                updatedPersonalizationArray.push({
                  ...r,
                  itemSize: row.size,
                  itemColor: row.color,
                });
              }
            } else {
              // Handle new personalization rows
              if (
                personalizationDropDown &&
                requiredLinesPersonalization &&
                dropDownValues?.length > 0
              ) {
                // Handle dropdown personalization
                dropDownValues.forEach((resData, keysData) => {
                  const baseValues =
                    isApplyToAllPersonalizationLines && firstRowValues?.[keysData]
                      ? {
                          displayText: firstRowValues[keysData].displayText,
                          color: firstRowValues[keysData].color,
                          font: firstRowValues[keysData].font,
                          location: firstRowValues[keysData].location,
                          notes: firstRowValues[keysData].notes,
                        }
                      : {
                          displayText: '',
                          color: getPersColor(product, productSettings),
                          font: getPersFont(product, productSettings),
                          location: getPersLocation(product, productSettings),
                          notes: getPersNote(product, productSettings),
                        };
                  updatedPersonalizationArray.push({
                    ...baseValues,
                    itemCode: product.itemCode,
                    itemColor: row.color,
                    itemId: product.id,
                    itemNo: product.productId,
                    itemSize: row.size,
                    orderId: '',
                    rowId: row.id,
                    rowQtyId: '',
                    rowQtyNo: index + 1,
                    sequance: 1 + keysData,
                    lineName: resData.personalizationDropDownRename,
                    isDrp: resData.isPersonalizationDropDown,
                    drpValue: resData.personalizationDropDownValue,
                    isEnabled: personalizationDropDown,
                    requiredLine: requiredLinesPersonalization,
                    cloneKey: row.cloneKey,
                  });
                });
              } else {
                // Handle standard personalization
                updatedPersonalizationArray.push({
                  color: getPersColor(product, productSettings),
                  location: getPersLocation(product, productSettings),
                  font: getPersFont(product, productSettings),
                  notes: getPersNote(product, productSettings),
                  displayText: '',
                  itemCode: product.itemCode,
                  itemColor: row.color,
                  itemId: product.id,
                  itemNo: product.productId,
                  itemSize: row.size,
                  orderId: '',
                  rowId: row.id,
                  rowQtyId: '',
                  rowQtyNo: index + 1,
                  sequance: 1,
                  cloneKey: row.cloneKey,
                });
              }
            }
          }
        }
      }
      // Clear display text if requested
      if (clear) {
        updatedPersonalizationArray = updatedPersonalizationArray.map((row) => ({
          ...row,
          displayText: '',
        }));
      }
      setPersonalizationArray(updatedPersonalizationArray);
    } catch (e) {
      console.log('error creating personalization rows', e);
    }
  };

  const deletePersonalization = (key: number) => {
    if (key) {
      const rowIds =
        personalizationArray?.filter((item) => item.cloneKey === key).map((item) => item.rowId) ??
        [];
      const updatedPersonalizationArray = personalizationArray?.filter(
        (persRow) => !rowIds.includes(persRow.rowId),
      );

      setItemsAdded((prev) => prev.filter((item) => item !== key));
      setPersonalizationArray(updatedPersonalizationArray ?? []);
    } else {
      setPersonalizationArray(null);
      setItemsAdded([]);
      setAddAll(false);
    }
  };

  return {
    personalizationCost,
    personalizationPerUnitPrice,
    personalizationSetupFee,
    personalizationDropDown,
    personalizationRequired,
    requiredLinesPersonalization,
    dropDownValues,
    personalizationArray,
    showPersonalizationCostInCart,
    showPersonalization,
    personalizationLabel,
    deletePersonalization,
    setPersonalizationArray,
    changePersonalizationArray,
    addPersonalization,
    addPersonalizationRow,
    deletePersonalizationRow,
    isApplyToAllPersonalizationLines,
  };
}

export default useProductPersonalizationState;
